// Copyright (c) 2023 embyt GmbH. All rights reserved.
// Author: Marc Planeck

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, IntlContext, IntlShape } from "react-intl";
import { Button } from "react-bootstrap";

import {
  IAction,
  ActionType,
  raiseAction,
  IActionTypeSetPeriod,
  ActionGraphCommand,
} from "actions";
import { IState } from "states/states";
import { IPeriodData } from "states/guistates";
import { PeriodIdentfier, TimePeriod } from "types";
import { RoutedProps, withRouter } from "view/reactrouterinterceptor";
import { isSet } from "shared/utils/utils";
import {
  getDateString,
  getGraphBoundaryDatesFromPeriod,
  getIdentifierForMainPanel,
  getPeriodStart,
  getUpdateCommandForIdentifier,
} from "guiutils";
import TrendWindowModel from "model/trendwindow";

import ModalTimeFilter from "view/modal/ModalTimeFilter";

import IconChevronLeft from "icons/iconChevronLeft";
import IconChevronsLeft from "icons/iconChevronsLeft";
import IconChevronRight from "icons/iconChevronRight";
import IconChevronsRight from "icons/iconChevronsRight";
import IconClock from "icons/iconClock";

import "./PeriodSelect.scss";

interface IPeriodSelectExtProps {
  className?: string;
  style?: React.CSSProperties;
}

interface IPeriodSelectProps {
  identifier: PeriodIdentfier;
  timePeriod: TimePeriod;
  startDate: number | undefined;
  endDate: number | undefined;
  isAtStart: boolean;
  isAtEnd: boolean;
  dataStartDate: number | undefined;
}

interface IPeriodSelectState {}

const mapStateToProps = (state: IState, ownProps: IPeriodSelectExtProps) => {
  const identifier = getIdentifierForMainPanel(state.router.panel);

  const curPeriod = state.gui[identifier] as IPeriodData | undefined;

  // zoom -> Month for Chargers, Day for all others (trend-data) --> if nothing is set!
  const xZoom =
    curPeriod?.timePeriod ?? (identifier === "ioPeriod" ? TimePeriod.MONTH : TimePeriod.DAY);

  const minTimestamp = TrendWindowModel.getDataBoundaries(state, identifier).min;

  const dataStart =
    isSet(minTimestamp) ? getPeriodStart(minTimestamp, xZoom, state.site.tzOffset ?? 0) : undefined;

  const { selectedEnd, graphStart, graphEnd } = getGraphBoundaryDatesFromPeriod(
    curPeriod!,
    state.site.tzOffset,
    xZoom,
  );

  const props: IPeriodSelectProps = {
    identifier,
    timePeriod: xZoom,
    startDate: graphStart,
    endDate: isSet(selectedEnd) ? graphEnd : undefined,
    isAtStart: isSet(graphStart) && isSet(dataStart) && dataStart >= graphStart,
    isAtEnd: !isSet(selectedEnd) || Date.now() / 1000 <= selectedEnd,
    dataStartDate: dataStart,
  };

  return props;
};

interface IPeriodSelectDispatch {
  onShowSelectTimerange: () => void;
  onPeriodScroll: (
    identifier: PeriodIdentfier,
    command: ActionGraphCommand,
    timePeriod: TimePeriod,
  ) => void;
}

const mapDispatchToProps = (
  dispatch: (action: IAction<any>) => void,
  ownProps: IPeriodSelectExtProps,
) => {
  const dispatchProps: IPeriodSelectDispatch = {
    onShowSelectTimerange: () => {
      dispatch(raiseAction(ActionType.ACT_EDIT_PERIOD));
    },
    onPeriodScroll: (
      identifier: PeriodIdentfier,
      command: ActionGraphCommand,
      timePeriod: TimePeriod,
    ) => {
      const payload: IActionTypeSetPeriod = {
        identifier,
        command,
        timePeriod,
      };
      dispatch(raiseAction(ActionType.ACT_CHANGE_PERIOD, payload));

      const updateCmd = getUpdateCommandForIdentifier(identifier);
      dispatch(raiseAction(updateCmd));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  selectDate: {
    id: "Select Date",
  },
});

class PeriodSelectComponent extends React.Component<
  IPeriodSelectProps & IPeriodSelectExtProps & IPeriodSelectDispatch & RoutedProps,
  IPeriodSelectState
> {
  static contextType = IntlContext;
  declare context: IntlShape;

  state: IPeriodSelectState = {};

  public render() {
    return (
      <>
        <div
          className={`periodselect headerbuttons mt-0 d-flex align-items-center justify-content-center justify-content-lg-between ${this.props.className ?? ""}`}
          style={this.props.style}
        >
          {this.renderDateSelector()}
        </div>
        {/* RENDER MODALS */}
        <ModalTimeFilter />
      </>
    );
  }

  private renderDateSelector() {
    const dateString = `${
      getDateString(
        this.props.timePeriod,
        this.props.startDate,
        this.props.endDate,
        this.props.isAtEnd,
        this.context.formatMessage({
          id: `thisperiod_${this.props.timePeriod === TimePeriod.CUSTOM ? TimePeriod.DAY : (this.props.timePeriod ?? "")}`,
        }),
        this.context.locale,
      ) ?? "-"
    }${
      this.props.isAtEnd === true ?
        ` - ${
          getDateString(
            this.props.timePeriod,
            this.props.startDate,
            this.props.endDate,
            false,
            this.context.formatMessage({
              id: `thisperiod_${this.props.timePeriod === TimePeriod.CUSTOM ? TimePeriod.DAY : (this.props.timePeriod ?? "")}`,
            }),
            this.context.locale,
          ) ?? "-"
        }`
      : ""
    }`;

    return (
      <div className="sh-btn-seperator mt-0">
        <Button
          className="btn-header d-none d-md-inline-block"
          variant={
            (
              !isSet(this.props.dataStartDate) ||
              this.props.isAtStart ||
              this.props.timePeriod === TimePeriod.ALL
            ) ?
              "secondary"
            : ""
          }
          onClick={() =>
            this.props.onPeriodScroll(
              this.props.identifier,
              ActionGraphCommand.START,
              this.props.timePeriod,
            )
          }
          disabled={
            !isSet(this.props.dataStartDate) ||
            this.props.isAtStart ||
            this.props.timePeriod === TimePeriod.ALL
          }
        >
          <IconChevronsLeft width={"20px"} height={"20px"} />
        </Button>
        <Button
          className="btn-header"
          variant={
            this.props.isAtStart || this.props.timePeriod === TimePeriod.ALL ? "secondary" : ""
          }
          onClick={(event) =>
            this.props.onPeriodScroll(
              this.props.identifier,
              ActionGraphCommand.BACKWARD,
              this.props.timePeriod,
            )
          }
          disabled={this.props.isAtStart || this.props.timePeriod === TimePeriod.ALL}
        >
          <IconChevronLeft width={"20px"} height={"20px"} />
        </Button>
        <Button
          className="btn-header text-nowrap text-truncate d-none d-md-inline-block"
          variant={""}
          onClick={() => {
            this.props.onShowSelectTimerange();
          }}
          title={dateString}
        >
          <FormattedMessage id={messages.selectDate.id} />
        </Button>
        <Button
          className="btn-header d-md-none"
          variant={""}
          onClick={() => {
            this.props.onShowSelectTimerange();
          }}
          title={dateString}
        >
          <IconClock width={"20px"} height={"20px"} />
        </Button>
        <Button
          className="btn-header"
          variant={
            this.props.isAtEnd || this.props.timePeriod === TimePeriod.ALL ? "secondary" : ""
          }
          onClick={() =>
            this.props.onPeriodScroll(
              this.props.identifier,
              ActionGraphCommand.FORWARD,
              this.props.timePeriod,
            )
          }
          disabled={this.props.isAtEnd || this.props.timePeriod === TimePeriod.ALL}
        >
          <IconChevronRight width={"20px"} height={"20px"} />
        </Button>
        <Button
          className="btn-header d-none d-md-inline-block"
          variant={
            this.props.isAtEnd || this.props.timePeriod === TimePeriod.ALL ? "secondary" : ""
          }
          onClick={() =>
            this.props.onPeriodScroll(
              this.props.identifier,
              ActionGraphCommand.END,
              this.props.timePeriod,
            )
          }
          disabled={this.props.isAtEnd || this.props.timePeriod === TimePeriod.ALL}
        >
          <IconChevronsRight width={"20px"} height={"20px"} />
        </Button>
      </div>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(PeriodSelectComponent));
